export enum FulfillmentGroupStatus {
  Cancelled = "CANCELLED",
  Closed = "CLOSED",
  Incomplete = "INCOMPLETE",
  Open = "OPEN",
  OnHold = "ON_HOLD",
  Scheduled = "SCHEDULED",
  Pending = "PENDING",
  Voided = "VOIDED",
  PaymentPending = "PAYMENT_PENDING",
  Merged = "MERGED",
  Unmerged = "UNMERGED",
  Invalid = "INVALID",
  Deleted = "DELETED",
  ReadyForPickup = "READY_FOR_PICKUP",
  ThirdPartyInProgress = "THIRD_PARTY_IN_PROGRESS",
  OpenNotReceived = "OPEN_NOT_RECEIVED", // This status is specific to Commentsold merchants. It indicates that a variant on the group is not received, so it isn't fulfillable
}

// these should never be shown to the user
export const HiddenStatuses = [
  FulfillmentGroupStatus.Merged,
  FulfillmentGroupStatus.Unmerged,
  FulfillmentGroupStatus.Invalid,
  FulfillmentGroupStatus.Deleted,
];

export const OpenLikeStatuses = [
  FulfillmentGroupStatus.Open,
  FulfillmentGroupStatus.OnHold,
  FulfillmentGroupStatus.PaymentPending,
  FulfillmentGroupStatus.Scheduled,
  FulfillmentGroupStatus.ReadyForPickup,
  FulfillmentGroupStatus.OpenNotReceived,
];

export const DoneStatuses = [
  FulfillmentGroupStatus.Closed,
  FulfillmentGroupStatus.Cancelled,
  FulfillmentGroupStatus.Voided,
  FulfillmentGroupStatus.Merged,
  FulfillmentGroupStatus.Unmerged,
  FulfillmentGroupStatus.Deleted,
  FulfillmentGroupStatus.ThirdPartyInProgress,
];

export function canPurchaseLabelStatus(
  status: FulfillmentGroupStatus | null | undefined,
): boolean {
  if (!status) {
    return false;
  }
  return [
    FulfillmentGroupStatus.Open,
    FulfillmentGroupStatus.PaymentPending,
  ].includes(status);
}

export function isDoneStatus(
  status: FulfillmentGroupStatus | null | undefined,
): boolean {
  if (!status) {
    return false;
  }
  return DoneStatuses.includes(status);
}

// most updates to the fulfillment group are not allowed when in one of these statuses
export function isEditingLockedStatus(
  status: FulfillmentGroupStatus | null | undefined,
): boolean {
  if (!status) {
    return false;
  }
  return isDoneStatus(status) || status === FulfillmentGroupStatus.Pending;
}

export function canSplitStatus(
  status: FulfillmentGroupStatus | null | undefined,
): boolean {
  if (!status) {
    return false;
  }
  return [
    FulfillmentGroupStatus.Open,
    FulfillmentGroupStatus.OnHold,
    FulfillmentGroupStatus.PaymentPending,
    FulfillmentGroupStatus.Scheduled,
    FulfillmentGroupStatus.OpenNotReceived,
  ].includes(status);
}

export function isClosedIsh(
  status: FulfillmentGroupStatus | null | undefined,
): boolean {
  if (!status) {
    return false;
  }
  return [
    FulfillmentGroupStatus.Closed,
    FulfillmentGroupStatus.Cancelled,
    FulfillmentGroupStatus.Voided,
  ].includes(status);
}

/**
 * Helper functions for inventory sync logic
 */
// These statuses should commit inventory for future fulfillment
export const OpenIshStatuses = [
  FulfillmentGroupStatus.Open,
  FulfillmentGroupStatus.OnHold,
  FulfillmentGroupStatus.Scheduled,
  FulfillmentGroupStatus.Pending,
  FulfillmentGroupStatus.PaymentPending,
  FulfillmentGroupStatus.ReadyForPickup,
  FulfillmentGroupStatus.ThirdPartyInProgress,
  FulfillmentGroupStatus.Incomplete,
  FulfillmentGroupStatus.OpenNotReceived,
];

export function isOpenIsh(
  status: FulfillmentGroupStatus | null | undefined,
): boolean {
  if (!status) {
    return false;
  }
  return OpenIshStatuses.includes(status);
}

// These statuses should deduct inventory
export function isCompleteIsh(
  status: FulfillmentGroupStatus | null | undefined,
): boolean {
  if (!status) {
    return false;
  }
  // This function is for statuses that represent successful completion
  return [FulfillmentGroupStatus.Closed].includes(status);
}

// These statuses should have a net zero impact on inventory
export function isCancelledIsh(
  status: FulfillmentGroupStatus | null | undefined,
): boolean {
  if (!status) {
    return false;
  }
  return [
    FulfillmentGroupStatus.Cancelled,
    FulfillmentGroupStatus.Merged,
    FulfillmentGroupStatus.Unmerged,
    FulfillmentGroupStatus.Voided,
    FulfillmentGroupStatus.Invalid,
    FulfillmentGroupStatus.Deleted,
  ].includes(status);
}
